import React, { memo } from 'react';
import { TableRow, type CellStyleParams } from './TableRow';
import { GroupedRow } from './GroupedRow';
import type { GroupedRowData, DataGridColumn } from '../DataGrid.types';
import type { AggregationConfig } from '../../utils/aggregations';
import type { DensityType } from './TableHeader';

interface TableBodyProps<T extends Record<string, unknown>> {
  rows: (GroupedRowData<T> | T)[];
  columns: DataGridColumn<T>[];
  expandedGroups: Set<string>;
  onToggleGroup: (groupId: string) => void;
  aggregations: AggregationConfig;
  onRowClick?: (row: T) => void;
  rowClassName?: string | ((row: T, index: number) => string);
  isGrouped: boolean;
  enableSelection?: boolean;
  isSelected?: (row: T) => boolean;
  onToggleSelect?: (row: T) => void;
  // Column reorder
  draggedColumnId?: string | null;
  getColumnStyle?: (columnId: string) => React.CSSProperties;
  // Enterprise Finance & B2B props
  density?: DensityType;
  enableCellFlash?: boolean;
  cellStyle?: (params: CellStyleParams<T>) => React.CSSProperties | undefined;
  // Grouping display options
  showGroupRowCount?: boolean;
}

function isGroupedRow<T>(row: GroupedRowData<T> | T): row is GroupedRowData<T> {
  return (row as GroupedRowData<T>).isGroupHeader === true;
}

// Helper function to generate stable row keys
function getRowKey<T extends Record<string, unknown>>(row: T, index: number): string {
  // Try common ID fields for stable keys
  if ('id' in row && row.id !== null && row.id !== undefined) {
    return `row_${String(row.id)}`;
  }
  if ('_id' in row && row._id !== null && row._id !== undefined) {
    return `row_${String(row._id)}`;
  }
  if ('key' in row && row.key !== null && row.key !== undefined) {
    return `row_${String(row.key)}`;
  }
  // Fallback to index-based key
  return `row_idx_${index}`;
}

function TableBodyComponent<T extends Record<string, unknown>>({
  rows,
  columns,
  expandedGroups,
  onToggleGroup,
  aggregations,
  onRowClick,
  rowClassName,
  isGrouped,
  enableSelection = false,
  isSelected,
  onToggleSelect,
  draggedColumnId,
  getColumnStyle,
  density = 'normal',
  enableCellFlash = false,
  cellStyle,
  showGroupRowCount = true
}: TableBodyProps<T>): React.ReactElement {
  const totalColumns = columns.length + (enableSelection ? 1 : 0);

  if (rows.length === 0) {
    return (
      <tbody>
        <tr>
          <td
            colSpan={totalColumns}
            style={{
              padding: 'var(--space-8) var(--space-4)',
              textAlign: 'center',
              color: 'var(--color-text-muted)',
              fontSize: 'var(--text-sm)'
            }}
          >
            Veri bulunamadı
          </td>
        </tr>
      </tbody>
    );
  }

  let dataRowIndex = 0;

  return (
    <tbody>
      {rows.map((row) => {
        if (isGrouped && isGroupedRow(row)) {
          return (
            <GroupedRow
              key={row.groupKey}
              group={row}
              columns={columns}
              isExpanded={expandedGroups.has(row.groupKey)}
              onToggle={() => onToggleGroup(row.groupKey)}
              aggregations={aggregations}
              draggedColumnId={draggedColumnId}
              getColumnStyle={getColumnStyle}
              density={density}
              showRowCount={showGroupRowCount}
            />
          );
        }

        const currentIndex = dataRowIndex++;
        const className = typeof rowClassName === 'function'
          ? rowClassName(row as T, currentIndex)
          : rowClassName;

        // Generate stable row key from row data if possible
        const rowKey = getRowKey(row as T, currentIndex);

        return (
          <TableRow
            key={rowKey}
            row={row as T}
            columns={columns}
            rowIndex={currentIndex}
            onClick={onRowClick}
            className={className}
            indent={isGrouped ? 1 : 0}
            isEven={currentIndex % 2 === 1}
            enableSelection={enableSelection}
            isSelected={isSelected?.(row as T) || false}
            onToggleSelect={onToggleSelect}
            draggedColumnId={draggedColumnId}
            getColumnStyle={getColumnStyle}
            density={density}
            enableCellFlash={enableCellFlash}
            cellStyle={cellStyle}
          />
        );
      })}
    </tbody>
  );
}

// Memoized export for performance
export const TableBody = memo(TableBodyComponent) as typeof TableBodyComponent;
